/**
 * Finanzen (B-01, BE-05) - rein und getestet. Alle Betraege in Cent
 * (Konvention 3); Ausgaben sind negativ gespeichert, hier aber als positive
 * Summen ausgewiesen.
 */
import { addMonths, differenceInDays, endOfMonth } from '@/lib/datetime';
import type { Account, Envelope, LocalDate, Subscription, Transaction } from '@/domain/types';

export const euro = (cent: number): string => (cent / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

/** "4,50", "1.234,56", "4.5", "12" -> Cent. Alles andere (leer, Buchstaben, 3 Nachkommastellen) -> null. */
export function euroParsen(text: string): number | null {
  const t = text.trim().replace(/\s|€/g, '');
  if (!t) return null;
  const mitKomma = t.includes(',');
  const norm = mitKomma ? t.replace(/\./g, '').replace(',', '.') : /^\d{1,3}(\.\d{3})+$/.test(t) ? t.replace(/\./g, '') : t;
  if (!/^\d+(\.\d{1,2})?$/.test(norm)) return null;
  return Math.round(Number(norm) * 100);
}

/** `yyyy-MM` eines Datums. */
export const monatVon = (d: LocalDate): string => d.slice(0, 7);

export interface MonatsSumme { monat: string; einnahmen: number; ausgaben: number; saldo: number; sparquote: number | null }

/** Einnahmen und Ausgaben eines Monats; Umbuchungen zaehlen nicht. Sparquote = Anteil des Einkommens, der uebrig bleibt (0..1, nie > 1). */
export function monatsSumme(tx: readonly Transaction[], monat: string): MonatsSumme {
  let einnahmen = 0;
  let ausgaben = 0;
  for (const t of tx) {
    if (monatVon(t.date) !== monat || t.kind === 'umbuchung') continue;
    if (t.kind === 'einnahme') einnahmen += Math.abs(t.amountCents);
    else ausgaben += Math.abs(t.amountCents);
  }
  const saldo = einnahmen - ausgaben;
  return { monat, einnahmen, ausgaben, saldo, sparquote: einnahmen > 0 ? Math.max(0, saldo) / einnahmen : null };
}

/** Die letzten `n` Monate bis einschliesslich dem von `heute`, aeltester zuerst. */
export function letzteMonate(tx: readonly Transaction[], heute: LocalDate, n = 6): MonatsSumme[] {
  return Array.from({ length: n }, (_, i) => monatsSumme(tx, monatVon(addMonths(heute, i - (n - 1)))));
}

/** Kontostand insgesamt: Startsaldo aller Konten plus alle Buchungen (Umbuchungen heben sich zwischen den Konten auf). */
export const guthaben = (konten: readonly Account[], tx: readonly Transaction[]): number =>
  konten.reduce((s, k) => s + k.openingBalanceCents, 0) + tx.reduce((s, t) => s + t.amountCents, 0);

export interface BudgetStand {
  /** `null` = Ausgaben ohne Kategorie. */
  envelope: Envelope | null;
  name: string;
  budget: number;
  ausgegeben: number;
  rest: number;
  /** Bis Monatsende erwartete Ausgaben bei gleichbleibendem Tempo; fuer vergangene Monate die tatsaechlichen. */
  hochrechnung: number;
  ueberBudget: boolean;
  /** Hochrechnung uebersteigt das Budget, obwohl es noch nicht ueberschritten ist. */
  droht: boolean;
}

const tag = (d: LocalDate) => Number(d.slice(8, 10));

/** Budgetstand je Kategorie fuer einen Monat, groesste Ausgaben zuerst. Kategorien ohne Budget und ohne Ausgaben entfallen. */
export function budgetStand(envelopes: readonly Envelope[], tx: readonly Transaction[], monat: string, heute: LocalDate): BudgetStand[] {
  const ausgaben = new Map<string | null, number>();
  for (const t of tx) {
    if (monatVon(t.date) !== monat || t.kind !== 'ausgabe') continue;
    const k = t.envelopeId ?? null;
    ausgaben.set(k, (ausgaben.get(k) ?? 0) + Math.abs(t.amountCents));
  }
  const laeuft = monatVon(heute) === monat;
  const faktor = laeuft ? tag(endOfMonth(heute)) / Math.max(1, tag(heute)) : 1;
  const zeile = (envelope: Envelope | null, name: string, budget: number): BudgetStand => {
    const ausgegeben = ausgaben.get(envelope?.id ?? null) ?? 0;
    const hochrechnung = Math.round(ausgegeben * faktor);
    return { envelope, name, budget, ausgegeben, rest: budget - ausgegeben, hochrechnung, ueberBudget: budget > 0 && ausgegeben > budget, droht: budget > 0 && ausgegeben <= budget && hochrechnung > budget };
  };
  const zeilen = envelopes.map((e) => zeile(e, e.name, e.monthlyBudgetCents));
  const ohne = zeile(null, 'Ohne Kategorie', 0);
  return [...zeilen, ...(ohne.ausgegeben > 0 ? [ohne] : [])].filter((z) => z.budget > 0 || z.ausgegeben > 0).sort((a, b) => b.ausgegeben - a.ausgegeben);
}

/** Wie viel pro Tag bis Monatsende noch ausgegeben werden darf; `null` ohne Budget oder nach dem Monat. */
export function tagesbudget(stand: readonly BudgetStand[], heute: LocalDate): number | null {
  const budget = stand.reduce((s, z) => s + z.budget, 0);
  if (budget <= 0) return null;
  const rest = budget - stand.filter((z) => z.budget > 0).reduce((s, z) => s + z.ausgegeben, 0);
  const tage = tag(endOfMonth(heute)) - tag(heute) + 1;
  return Math.max(0, Math.round(rest / tage));
}

const PRO_MONAT: Record<Subscription['cycle'], number> = { monatlich: 1, quartal: 3, jaehrlich: 12 };

/** Kosten eines Abos auf den Monat umgerechnet. */
export const aboMonatlich = (s: Pick<Subscription, 'amountCents' | 'cycle'>): number => Math.round(s.amountCents / PRO_MONAT[s.cycle]);

/** Letzter Tag, an dem noch gekuendigt werden kann: das gesetzte Datum, sonst naechste Zahlung minus Kuendigungsfrist. */
export function kuendigenBis(s: Subscription): LocalDate | null {
  if (s.cancelBy) return s.cancelBy;
  if (s.noticePeriodDays === undefined) return null;
  const d = new Date(`${s.nextDue}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - s.noticePeriodDays);
  return d.toISOString().slice(0, 10);
}

export interface AboZeile { abo: Subscription; kuendigenBis: LocalDate | null; tageBisKuendigung: number | null; tageBisZahlung: number; dringend: boolean }

/** Abos nach naechster Zahlung; `dringend`, wenn die Kuendigungsfrist in hoechstens 30 Tagen ablaeuft. */
export function aboZeilen(abos: readonly Subscription[], heute: LocalDate): AboZeile[] {
  return abos.map((abo) => {
    const k = kuendigenBis(abo);
    const tageK = k ? differenceInDays(heute, k) : null;
    return { abo, kuendigenBis: k, tageBisKuendigung: tageK, tageBisZahlung: differenceInDays(heute, abo.nextDue), dringend: tageK !== null && tageK >= 0 && tageK <= 30 };
  }).sort((a, b) => a.abo.nextDue.localeCompare(b.abo.nextDue));
}

// --- Schnell-Eintrag (B-01) ----------------------------------------------------

export interface SchnellBuchung { kind: 'ausgabe' | 'einnahme'; cent: number; payee: string; envelopeId?: string }

/**
 * "€ 4,50 Döner", "4,50 Döner #Essen", "+800 Taschengeld": Betrag, optional
 * fuehrendes "+" (Einnahme) oder "-" und "€", dann das Wofuer; `#Name` waehlt
 * eine vorhandene Kategorie (Gross-/Kleinschreibung egal). Sonst `null`.
 */
export function schnellBuchung(text: string, kategorien: readonly Pick<Envelope, 'id' | 'name'>[]): SchnellBuchung | null {
  const m = /^\s*([+-])?\s*€?\s*([\d.,]+)\s*€?\s*(.*)$/.exec(text);
  if (!m) return null;
  const cent = euroParsen(m[2]!);
  if (!cent) return null;
  let rest = m[3]!.trim();
  let envelopeId: string | undefined;
  const tag = /(?:^|\s)#(\S+)/.exec(rest);
  if (tag) {
    const k = kategorien.find((e) => e.name.toLowerCase().replace(/\s+/g, '') === tag[1]!.toLowerCase());
    if (k) { envelopeId = k.id; rest = rest.replace(tag[0], ' ').trim(); }
  }
  return { kind: m[1] === '+' ? 'einnahme' : 'ausgabe', cent, payee: rest, ...(envelopeId ? { envelopeId } : {}) };
}

// --- Konten und wiederkehrende Buchungen (B-02) --------------------------------

export const kontostand = (konto: Account, tx: readonly Transaction[]): number =>
  konto.openingBalanceCents + tx.filter((t) => t.accountId === konto.id).reduce((s, t) => s + t.amountCents, 0);

const SCHRITT: Record<Subscription['cycle'], number> = { monatlich: 1, quartal: 3, jaehrlich: 12 };

/** Die Zahlungstage eines Abos von `nextDue` bis einschliesslich `heute` (hoechstens 36) und der Tag danach. */
export function faelligeTermine(abo: Pick<Subscription, 'nextDue' | 'cycle'>, heute: LocalDate): { termine: LocalDate[]; naechste: LocalDate } {
  const termine: LocalDate[] = [];
  let i = 0;
  let d = abo.nextDue;
  while (d <= heute && termine.length < 36) {
    termine.push(d);
    i += 1;
    d = addMonths(abo.nextDue, i * SCHRITT[abo.cycle]);
  }
  return { termine, naechste: d };
}

/** Monats- und Kategorieauswertung (B-04): Ausgaben je Kategorie in einem Monat, groesste zuerst. */
export function ausgabenJeKategorie(envelopes: readonly Envelope[], tx: readonly Transaction[], monat: string): { name: string; id: string | null; cent: number }[] {
  const summe = new Map<string | null, number>();
  for (const t of tx) {
    if (monatVon(t.date) !== monat || t.kind !== 'ausgabe') continue;
    summe.set(t.envelopeId ?? null, (summe.get(t.envelopeId ?? null) ?? 0) + Math.abs(t.amountCents));
  }
  const name = new Map(envelopes.map((e) => [e.id, e.name]));
  return [...summe.entries()].map(([id, cent]) => ({ id, cent, name: id ? name.get(id) ?? 'Gelöschte Kategorie' : 'Ohne Kategorie' })).sort((a, b) => b.cent - a.cent);
}

/** Alle 12 Monate eines Jahres. */
export const jahresMonate = (tx: readonly Transaction[], jahr: number): MonatsSumme[] =>
  Array.from({ length: 12 }, (_, i) => monatsSumme(tx, `${jahr}-${String(i + 1).padStart(2, '0')}`));
